import * as React from 'react';

import { Button, FormPasswordField, FormTextField, Typography } from 'omakase';
import { Form, Formik } from 'formik';
import { Grid } from 'ui';
import { isEmail, requireFields } from '@tock/shared/utils/FormValidators';

interface Props {
  hasEmailChangeInProgress: boolean;
  fromEmail: string;
  toEmail: string;
  onSubmit(values: FormFields): void;
}

interface FormFields {
  email: string;
  password: string;
}

const validate = (values: FormFields) => {
  const errors = requireFields(values, 'email', 'password');
  if (values.email && !isEmail(values.email)) {
    errors.email = 'Not a valid email address';
  }
  return errors;
};

const ChangeEmailForm: React.FC<Props> = ({
  hasEmailChangeInProgress,
  fromEmail,
  toEmail,
  onSubmit,
}) => {
  return (
    <Formik<FormFields>
      initialValues={{ email: '', password: '' }}
      onSubmit={onSubmit}
      validate={validate}
    >
      {({ isSubmitting, isValidating, isValid, dirty }) => (
        <Form className="Form--default Form--no-padding">
          <Grid container spacing={2}>
            {hasEmailChangeInProgress && (
              <Grid item>
                <Typography color="inherit" component="span" variant="body02">
                  You've requested that we change your email from {fromEmail} to {toEmail}. We've
                  sent a confirmation email to {toEmail} -- please open it and click the
                  confirmation link to change your email to {toEmail}.
                </Typography>
              </Grid>
            )}

            <Grid item>
              <Typography color="inherit" component="span" variant="body02">
                In order to update your email, please verify your password for security purposes.
              </Typography>
            </Grid>
            <Grid item>
              <Typography color="inherit" component="span" variant="body02">
                A confirmation email with an activation link will be sent to your new email address
                - clicking on the link will update your email.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormPasswordField
                id="password"
                name="password"
                label="Password"
                variant="inverse"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                id="email"
                name="email"
                label="New Email Address"
                variant="inverse"
                fullWidth
              />
            </Grid>

            <Grid item>
              <Button
                size="large"
                disabled={isSubmitting || isValidating || !isValid || !dirty}
                type="submit"
                variant="primary"
              >
                Update email
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ChangeEmailForm;
